import { StrictMode } from "react"

import { TanStackDevtools } from "@tanstack/react-devtools"
import { QueryClientProvider } from "@tanstack/react-query"
import { ReactQueryDevtoolsPanel } from "@tanstack/react-query-devtools"
import { createRoot } from "react-dom/client"
import { ErrorBoundary } from "react-error-boundary"
import { HelmetProvider } from "react-helmet-async"

import { AlertDialogContainer } from "@/components/ui/cn/alert-dialog"
import { ToastContainer } from "@/components/ui/cn/toast"
import { TooltipProvider } from "@/components/ui/cn/tooltip"
import { IS_DEV } from "@/lib/constants"
import { ErrorState } from "@/src/components"
import { AppPoller } from "@/src/components/AppPoller"
import { LumenDevtoolsPanel } from "@/src/components/Devtools"
import { initPosthog } from "@/src/lib/posthog"
import "@/src/lib/zod-config"

import "../sentry.client.config"

import "../styles/tailwind.css"
import { queryClient } from "@/src/lib/queryClient"
import { FeatureFlagsProvider } from "@/src/providers/FeatureFlags"

import { AuthProvider } from "./providers/Auth"
import { ThemeProvider } from "./providers/theme-provider"
import { ReactRouter, router } from "./router/index"

initPosthog(router.routes)

const rootEl = document.getElementById("root")!

createRoot(rootEl).render(
  <StrictMode>
    <MyApp />
  </StrictMode>,
)

function MyApp() {
  return (
    <>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ErrorBoundary
            fallbackRender={({ error, resetErrorBoundary }) => (
              <ErrorState
                title="Something went wrong"
                subTitle="Try again or contact an administrator"
                retryLabel="Try again"
                onRetry={resetErrorBoundary}
                rawError={error}
              />
            )}
          >
            <HelmetProvider>
              <FeatureFlagsProvider>
                <ThemeProvider storageKey="lumen-ui-theme">
                  <TooltipProvider>
                    <AppPoller />
                    <ReactRouter />
                  </TooltipProvider>
                </ThemeProvider>
              </FeatureFlagsProvider>
            </HelmetProvider>
            <ToastContainer />
            <AlertDialogContainer />
            {IS_DEV && !import.meta.env.VITE_HIDE_DEVTOOLS ? (
              <TanStackDevtools
                config={{
                  defaultOpen: false,
                }}
                plugins={[
                  {
                    name: "TanStack Query",
                    render: <ReactQueryDevtoolsPanel style={{ height: "100%" }} />,
                    defaultOpen: true,
                  },
                  {
                    name: "Lumen GraphQL",
                    render: <LumenDevtoolsPanel />,
                    defaultOpen: true,
                  },
                ]}
              />
            ) : null}
          </ErrorBoundary>
        </AuthProvider>
      </QueryClientProvider>
    </>
  )
}
